import { FormProvider, getFormProps } from '@conform-to/react'
import {
	Badge,
	Box,
	Flex,
	IconButton,
	Tooltip,
	VisuallyHidden,
} from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { RxCross1, RxMinus, RxPlus } from 'react-icons/rx'

import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { ProductName } from '#app/components/common/product-name'
import { ErrorMessage } from '#app/components/ui/error-message/error-message.tsx'
import { Image, IMAGE_FILTERS } from '#app/components/ui/image'
import { Price } from '#app/components/ui/price/price.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type GetProductOption } from '#app/models/ecommerce-providers/sylius/shop/fetch/product-option/get-product-option'
import { type GetProductVariant } from '#app/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant'
import { type ProductImageNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product-image'
import { type ProductOptionValueNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product-option-value'
import { type Locale } from '#app/models/locale'
import {
	CART_FORM_REMOVE_ITEM,
	CART_FORM_SET_QUANTITY,
	CART_INPUTS,
} from '#app/routes/($lang).cart/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { CART_ACTION_TYPES, useCart } from '#app/utils/client/cart-context.tsx'
import { testId } from '#app/utils/client/test-id.ts'
import { useCartItem } from '#app/utils/hooks/use-cart-item.ts'

/* -------------------------------------------------------------------------------------------------
 * TEST_IDS
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	cartListItem = 'cart-list-item',
	removeCartItem = 'remove-cart-item',
	decrementCartItem = 'decrement-cart-item',
	incrementCartItem = 'increment-cart-item',
}

/* -------------------------------------------------------------------------------------------------
 * CartListItem
 * -----------------------------------------------------------------------------------------------*/

type CartListItemProps = {
	image: ProductImageNormalized
	locale: Locale
	name: string
	productSlug: string
	optionValues: ProductOptionValueNormalized[] | undefined
	orderItemId: number
	setApp: React.Dispatch<React.SetStateAction<App>>
	variant: GetProductVariant
}

const CartListItem: React.FC<CartListItemProps> = (props) => {
	const {
		locale,
		optionValues,
		image,
		name,
		productSlug,
		orderItemId,
		setApp,
		variant,
	} = props

	const {
		item,
		setQuantityFetcher,
		removeItemFetcher,
		setQuantityFormValues,
		setQuantityForm,
		setQuantityFields,
		increaseQuantityFormValues,
		increaseQuantityForm,
		increaseQuantityFields,
		decreaseQuantityFormValues,
		decreaseQuantityForm,
		decreaseQuantityFields,
		removeItemFormValues,
		removeItemForm,
		removeItemFields,
		t,
		location,
		isChanged,
		setIsChanged,
		pending,
		actionInit,
		errorMessage,
	} = useCartItem({
		setApp,
		name,
		orderItemId,
		slug: productSlug,
		variantCode: variant.code!,
	})

	const { cartDispatch } = useCart()

	return (
		<li
			className={cn('app-ImageShadowContainer my-2 p-4', {
				'animate-pulse': !!pending.length,
			})}
			{...testId(TEST_IDS.cartListItem)}
		>
			<Flex align="center" gapX="4">
				<Box position="relative" className="block aspect-square w-24">
					<Link
						to={`/${locale}/products/${productSlug}`}
						prefetch="viewport"
						onClick={() =>
							setApp((prev) => ({
								...prev,
								displaySidebar: false,
							}))
						}
					>
						<Image
							alt={name}
							filter={IMAGE_FILTERS.gridFilter}
							src={image.path!}
							className="app-ImageShadow absolute inset-0"
						/>
					</Link>
				</Box>
				<Flex
					align={{ xs: 'center' }}
					justify={{ xs: 'between' }}
					direction={{ initial: 'column', xs: 'row' }}
					gap="4"
				>
					<Box flexGrow="1">
						<Link to={`/${locale}/products/${productSlug}`} prefetch="viewport">
							<ProductName name={name} />
						</Link>
						<Flex asChild direction="column" align="start" gapY="2">
							<ul>
								{optionValues?.map(({ code, value, option }) => (
									<li
										key={code}
										className="mt-1 inline-flex items-center justify-center gap-x-1"
									>
										<Badge color="gray">
											{(option as GetProductOption).name!.replace(
												' |',
												`: ${value}`,
											)}
										</Badge>
									</li>
								))}
							</ul>
						</Flex>
					</Box>
					<Price locale={locale} color="gray">
						{item?.unitPrice!}
					</Price>
				</Flex>
			</Flex>
			<Flex gapX="1" mt="5" align="center">
				<FormProvider context={removeItemForm.context}>
					<Tooltip content={t('items.common.removeCartItem')}>
						<removeItemFetcher.Form
							method="post"
							action={`/${locale}${CART_FORM_REMOVE_ITEM.action}${location.search}`}
							{...getFormProps(removeItemForm)}
							preventScrollReset
							{...testId(TEST_IDS.removeCartItem)}
						>
							<FormDefaults
								form={removeItemForm}
								fields={removeItemFields}
								values={removeItemFormValues}
							/>
							<IconButton
								variant="soft"
								radius="full"
								color="red"
								type="submit"
								disabled={actionInit}
							>
								<VisuallyHidden>{t('common.removeFromCart')}</VisuallyHidden>
								<RxCross1 />
							</IconButton>
						</removeItemFetcher.Form>
					</Tooltip>
				</FormProvider>
				<FormProvider context={setQuantityForm.context}>
					<setQuantityFetcher.Form
						method="post"
						action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
						{...getFormProps(setQuantityForm)}
						className="mx-3 grow"
						preventScrollReset
						onChange={() => setIsChanged(true)}
					>
						<FormDefaults
							form={setQuantityForm}
							fields={setQuantityFields}
							values={setQuantityFormValues}
						/>
						<TextField
							meta={setQuantityFields[CART_INPUTS.quantity]}
							t={t}
							type="number"
							onBlur={(e) => {
								if (isChanged) {
									setQuantityFetcher.submit(e.currentTarget.form)

									cartDispatch({
										type: CART_ACTION_TYPES.UPDATE_ITEM_FROM_CART,
										payload: {
											quantity: Number(setQuantityForm.value!.quantity),
											id: orderItemId,
										},
									})
								}
							}}
							translationOptions={{}}
							disabled={actionInit}
						>
							{t('label.quantity')}
						</TextField>
					</setQuantityFetcher.Form>
				</FormProvider>
				<FormProvider context={decreaseQuantityForm.context}>
					<Tooltip content={t('items.common.decrementCartItemQuantity')}>
						<setQuantityFetcher.Form
							method="post"
							action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
							{...getFormProps(decreaseQuantityForm)}
							preventScrollReset
							{...testId(TEST_IDS.decrementCartItem)}
						>
							<FormDefaults
								form={decreaseQuantityForm}
								fields={decreaseQuantityFields}
								values={decreaseQuantityFormValues}
							/>
							<IconButton
								variant="soft"
								radius="full"
								type="submit"
								disabled={(item?.quantity || 0) - 1 <= 0 || actionInit}
							>
								<VisuallyHidden>{t('common.subtractItem')}</VisuallyHidden>
								<RxMinus />
							</IconButton>
						</setQuantityFetcher.Form>
					</Tooltip>
				</FormProvider>
				<FormProvider context={increaseQuantityForm.context}>
					<Tooltip content={t('items.common.incrementCartItemQuantity')}>
						<setQuantityFetcher.Form
							method="post"
							action={`/${locale}${CART_FORM_SET_QUANTITY.action}${location.search}`}
							{...getFormProps(increaseQuantityForm)}
							preventScrollReset
							{...testId(TEST_IDS.incrementCartItem)}
						>
							<FormDefaults
								form={increaseQuantityForm}
								fields={increaseQuantityFields}
								values={increaseQuantityFormValues}
							/>
							<IconButton
								variant="soft"
								radius="full"
								type="submit"
								disabled={actionInit}
							>
								<VisuallyHidden>{t('common.addItem')}</VisuallyHidden>
								<RxPlus />
							</IconButton>
						</setQuantityFetcher.Form>
					</Tooltip>
				</FormProvider>
			</Flex>
			{!!errorMessage && (
				<ErrorMessage
					{...errorMessage}
					calloutProps={{
						className: 'mt-4',
						size: '1',
					}}
				/>
			)}
		</li>
	)
}
CartListItem.displayName = 'CartListItem'

/* -----------------------------------------------------------------------------------------------*/

export { CartListItem, TEST_IDS as CART_LIST_ITEM_TEST_IDS }
